import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify"; // Make sure to import toast

const BusBookings = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const getTodayDate = () => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const localDate = new Date(today.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split("T")[0];
  };

  const [pageLoading, setPageLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [rawResponse, setRawResponse] = useState({});

  // Pagination States
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter States - Both dates default to today
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [passengerName, setPassengerName] = useState("");
  const [bookingNo, setBookingNo] = useState("");
  const [bookingStatus, setBookingStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [source, setSource] = useState("CLIENT");

  // Edit Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editBooking, setEditBooking] = useState({
    bookingId: "",
    passengerName: "",
    sourceCity: "",
    destinationCity: "",
    departureDate: "",
    busType: "",
    operatorName: "",
    journeyType: "",
    bookingStatus: "",
    paymentStatus: "",
    pnr: "",
    supplierBookingId: "",
    baseFare: "",
    tax: "",
    convenienceFee: "",
    gst: "",
    gstAmount: "",
    totalFare: "",
    totalPrice: "",
    markup: "",
    discount: "",
    paymentGateway: "",
    paymentGatewayAmount: "",
    boardingPoint: "",
    droppingPoint: "",
    passengerDetails: "",
    noOfPassengers: "",
    customerPhone: "",
    supplierName: ""
  });
  const [editLoading, setEditLoading] = useState(false);

  // Email Modal States
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailType, setEmailType] = useState(""); // "customer" or "agent"
  const [currentBookingId, setCurrentBookingId] = useState("");

  const GetData = async (pNum = 1) => {
    if (!user?.cid) return;
    setPageLoading(true);

    const queryParams = new URLSearchParams({
      passengerName: passengerName || "",
      bookingNo: bookingNo || "",
      bookingStatus: bookingStatus || "",
      paymentStatus: paymentStatus || "",
      fromDate: fromDate || "",
      toDate: toDate || "",
      source: source || "",
      pageNumber: pNum,
      pageSize: pageSize
    }).toString();

    try {
      const url = `${config.GETPARENTCOMPANYBUSBOOKINGS}/${user?.cid}?${queryParams}`;
      const res = await apiService.post(url);

      if (res) {
        setLists(res.data || []);
        setPageNumber(res.pageNumber || 1);
        setPageSize(res.pageSize || 20);
        setTotalPages(res.totalPages || 0);
        setTotalRecords(res.totalRecords || 0);
        setRawResponse(res);
      }
    } catch (error) {
      console.error("Failed to fetch bus bookings:", error);
      setLists([]);
    } finally {
      setPageLoading(false);
    }
  };

  const handleConfirm = async (e, busBookingId) => {
    setPageLoading(true);
    try {
      await apiService.post(`${config.CONFIRMHOLDBUSBOOKING}/${busBookingId}`);
      navigate("/bus/booking/" + busBookingId);
    } catch (error) {
      console.error("Error confirming booking:", error);
    } finally {
      setPageLoading(false);
    }
  };

  // Delete handler - Added this function
  const handleDelete = async (busBookingId, bookingNo) => {
    // Show confirmation dialog
    if (!window.confirm(`Are you sure you want to delete booking #${bookingNo}?`)) {
      return;
    }

    setPageLoading(true);
    try {
      // Call the delete API with isDeleted = true
      const url = `${config.DELETEBUSBOOKING}/${busBookingId}`;
      const result = await apiService.put(url);

      if (result) {
        toast.success(`Booking deleted successfully`);
        // Refresh the list after deletion
        GetData(pageNumber);
      } else {
        toast.error("Failed to delete booking");
      }
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast.error("An error occurred while deleting the booking");
    } finally {
      setPageLoading(false);
    }
  };

  // Handle sending email to customer
  const handleSendCustomerEmail = async (bookingId) => {
    setCurrentBookingId(bookingId);
    setEmailType("customer");
    setShowEmailModal(true);
    setEmailLoading(true);
    setEmailSuccess(false);
    setEmailError("");

    try {
      const url = `${config.SENDCUSTOMEREMAIL}/${bookingId}`;
      const response = await apiService.post(url);

      if (response && response.succeeded) {
        setEmailSuccess(true);
        setEmailLoading(false);
      } else {
        setEmailError(response.message || "Failed to send customer email");
        setEmailLoading(false);
      }
    } catch (error) {
      console.error("Error sending customer email:", error);
      setEmailError("Error sending customer email. Please try again.");
      setEmailLoading(false);
    }
  };

  // Handle sending email to agent
  const handleSendAgentEmail = async (bookingId) => {
    setCurrentBookingId(bookingId);
    setEmailType("agent");
    setShowEmailModal(true);
    setEmailLoading(true);
    setEmailSuccess(false);
    setEmailError("");

    try {
      const url = `${config.SENDAGENTEMAIL}/${bookingId}`;
      const response = await apiService.post(url);

      if (response && response.succeeded) {
        setEmailSuccess(true);
        setEmailLoading(false);
      } else {
        setEmailError(response.message || "Failed to send agent email");
        setEmailLoading(false);
      }
    } catch (error) {
      console.error("Error sending agent email:", error);
      setEmailError("Error sending agent email. Please try again.");
      setEmailLoading(false);
    }
  };

  // Close Email Modal
  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setEmailLoading(false);
    setEmailSuccess(false);
    setEmailError("");
    setEmailType("");
    setCurrentBookingId("");
  };

  // Open Edit Modal with booking data
  const handleEditClick = (booking) => {
    setEditBooking({
      bookingId: booking.busBookingId || "",
      passengerName: booking.passengerName || "",
      sourceCity: booking.sourceCity || "",
      destinationCity: booking.destinationCity || "",
      departureDate: booking.departureDate ? moment(booking.departureDate).format("YYYY-MM-DDTHH:mm") : "",
      busType: booking.busType || "",
      operatorName: booking.operatorName || "",
      journeyType: booking.journeyType || "",
      bookingStatus: booking.bookingStatus || "",
      paymentStatus: booking.paymentStatus || "",
      pnr: booking.pnr || "",
      supplierBookingId: booking.supplierBookingId || "",
      baseFare: booking.baseFare || "",
      tax: booking.tax || "",
      convenienceFee: booking.convenienceFee || "",
      gst: booking.gst || "",
      gstAmount: booking.gstAmount || "",
      totalFare: booking.totalFare || "",
      totalPrice: booking.totalPrice || "",
      markup: booking.markup || "",
      discount: booking.discount || "",
      paymentGateway: booking.paymentGateway || "",
      paymentGatewayAmount: booking.paymentGatewayAmount || "",
      boardingPoint: booking.boardingPoint || "",
      droppingPoint: booking.droppingPoint || "",
      passengerDetails: booking.passengerDetails || "",
      noOfPassengers: booking.noOfPassengers || "",
      customerPhone: booking.customerPhone || "",
      supplierName: booking.supplierName || ""
    });
    setShowEditModal(true);
  };

  // Close Edit Modal
  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditBooking({
      bookingId: "",
      passengerName: "",
      sourceCity: "",
      destinationCity: "",
      departureDate: "",
      busType: "",
      operatorName: "",
      journeyType: "",
      bookingStatus: "",
      paymentStatus: "",
      pnr: "",
      supplierBookingId: "",
      baseFare: "",
      tax: "",
      convenienceFee: "",
      gst: "",
      gstAmount: "",
      totalFare: "",
      totalPrice: "",
      markup: "",
      discount: "",
      paymentGateway: "",
      paymentGatewayAmount: "",
      boardingPoint: "",
      droppingPoint: "",
      passengerDetails: "",
      noOfPassengers: "",
      customerPhone: "",
      supplierName: ""
    });
  };

  // Handle Edit Input Change
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditBooking(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save Edited Booking
  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      const payload = {
        bookingId: editBooking.bookingId,
        passengerName: editBooking.passengerName || null,
        sourceCity: editBooking.sourceCity || null,
        destinationCity: editBooking.destinationCity || null,
        departureDate: editBooking.departureDate || null,
        busType: editBooking.busType || null,
        operatorName: editBooking.operatorName || null,
        journeyType: editBooking.journeyType || null,
        bookingStatus: editBooking.bookingStatus || null,
        paymentStatus: editBooking.paymentStatus || null,
        pnr: editBooking.pnr || null,
        supplierBookingId: editBooking.supplierBookingId || null,
        baseFare: editBooking.baseFare ? parseFloat(editBooking.baseFare) : null,
        tax: editBooking.tax ? parseFloat(editBooking.tax) : null,
        convenienceFee: editBooking.convenienceFee ? parseFloat(editBooking.convenienceFee) : null,
        gst: editBooking.gst ? parseFloat(editBooking.gst) : null,
        gstAmount: editBooking.gstAmount ? parseFloat(editBooking.gstAmount) : null,
        totalFare: editBooking.totalFare ? parseFloat(editBooking.totalFare) : null,
        totalPrice: editBooking.totalPrice ? parseFloat(editBooking.totalPrice) : null,
        markup: editBooking.markup ? parseFloat(editBooking.markup) : null,
        discount: editBooking.discount ? parseFloat(editBooking.discount) : null,
        paymentGateway: editBooking.paymentGateway || null,
        paymentGatewayAmount: editBooking.paymentGatewayAmount ? parseFloat(editBooking.paymentGatewayAmount) : null,
        boardingPoint: editBooking.boardingPoint || null,
        droppingPoint: editBooking.droppingPoint || null,
        passengerDetails: editBooking.passengerDetails || null
      };

      const url = `${config.UPDATEBUSBOOKING}`;
      const response = await apiService.put(url, payload);

      if (response && response.succeeded) {
        alert("Booking updated successfully!");
        handleCloseModal();
        GetData(pageNumber);
      } else {
        alert(response.message || "Failed to update booking");
      }
    } catch (error) {
      console.error("Error updating booking:", error);
      alert("Error updating booking. Please try again.");
    } finally {
      setEditLoading(false);
    }
  };

  // Pagination Logic
  const GetPageData = (number) => {
    setPageNumber(number);
    GetData(number);
  };

  const GetFirstPageData = () => {
    if (rawResponse.previousPage !== null) GetData(1);
  };

  const GetLastPageData = () => {
    if (rawResponse.nextPage !== null) GetData(totalPages);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => (
    <li
      className={`page-item ${pageNumber === number ? "active" : ""}`}
      key={number}
      onClick={() => GetPageData(number)}
    >
      <Link className="page-link">{number}</Link>
    </li>
  ));

  // Search & Reset
  const handleSearch = () => {
    setPageNumber(1);
    GetData(1);
  };

  const handleReset = () => {
    setFromDate(getTodayDate());
    setToDate(getTodayDate());
    setPassengerName("");
    setBookingNo("");
    setBookingStatus("");
    setPaymentStatus("");
    setSource("CLIENT");
    setPageNumber(1);
    setTimeout(() => GetData(1), 100);
  };

  useEffect(() => {
    if (user && !loading) {
      GetData(pageNumber);
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  // Format currency
  const formatCurrency = (amount) => {
    return `INR ${amount}`;
  };
  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case "CONFIRMED":
      case "BOOKED":
        return "success";
      case "BLOCKED":
      case "HOLD":
      case "PENDING":
        return "warning";
      case "CANCELLED":
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Get payment badge color
  const getPaymentBadgeColor = (status) => {
    switch (status?.toUpperCase()) {
      case "SUCCESS":
      case "PAID":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <div id="layout-wrapper">
      <Header User={user} activeSection="buses" />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="card-body">
                  {/* --- Filter Section --- */}
                  <div className="row mb-4 g-3 bg-light p-3 rounded">
                    <div className="col-md-2">
                      <label className="form-label">From Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">To Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Source</label>
                      <select
                        className="form-select"
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                      >
                        <option value="CLIENT">PORTAL</option>
                        <option value="CUSTOMER">CUSTOMER</option>
                        <option value="CORPORATE">CORPORATE</option>
                        <option value="AGENT">AGENT</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Passenger Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search Name..."
                        value={passengerName}
                        onChange={(e) => setPassengerName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Booking No</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Booking No..."
                        value={bookingNo}
                        onChange={(e) => setBookingNo(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Booking Status</label>
                      <select
                        className="form-select"
                        value={bookingStatus}
                        onChange={(e) => setBookingStatus(e.target.value)}
                      >
                        <option value="">All Status</option>
                        <option value="BOOKED">BOOKED</option>
                        <option value="CONFIRMED">CONFIRMED</option>
                        <option value="BLOCKED">BLOCKED</option>
                        <option value="HOLD">HOLD</option>
                        <option value="PENDING">PENDING</option>
                        <option value="CANCELLED">CANCELLED</option>
                        <option value="FAILED">FAILED</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Payment Status</label>
                      <select
                        className="form-select"
                        value={paymentStatus}
                        onChange={(e) => setPaymentStatus(e.target.value)}
                      >
                        <option value="">All Payment</option>
                        <option value="SUCCESS">SUCCESS</option>
                        <option value="PAID">PAID</option>
                        <option value="PENDING">PENDING</option>
                        <option value="FAILED">FAILED</option>
                      </select>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <button className="btn btn-primary me-2 w-100" onClick={handleSearch}>
                        <i className="ri-search-line me-1"></i> Search
                      </button>
                      <button className="btn btn-danger w-100" onClick={handleReset}>
                        <i className="ri-refresh-line me-1"></i> Reset
                      </button>
                    </div>
                  </div>

                  {/* --- Table Section --- */}
                  <div className="table-responsive table-card p-2">
                    <table className="table align-middle" id="busBookingTable">
                      <thead className="bg-soft-info bg-gradient">
                        <tr>
                          <th>#</th>
                          <th>Supplier</th>
                          <th>Booking Details</th>
                          <th>Route & Date</th>
                          <th>Operator & Bus</th>
                          <th>Passenger</th>
                          <th>Booking Status</th>
                          <th>Payment Status</th>
                          <th>Amount</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody className="list form-check-all">
                        {pageLoading ? (
                          <tr>
                            <td colSpan={10} className="text-center">
                              <img
                                src="/assets/images/loader.webp"
                                width="64px"
                                height="64px"
                                alt="loader"
                                loading="lazy" />
                              <p>Loading, Please wait...</p>
                            </td>
                          </tr>
                        ) : lists.length > 0 ? (
                          lists.map((v, index) => (
                            <tr key={v.busBookingId}>
                              <td>{(pageNumber - 1) * pageSize + (index + 1)}</td>
                              <td>
                                {v.supplierBrandName || v.supplierCompanyName || "N/A"}

                              </td>
                              <td>
                                {v.bookingNo}
                                <br />
                                <small className="text-muted">PNR: {v.pnr || "N/A"}</small>
                                <br />
                                <small className="text-muted">SupplierBookingId: {v.supplierBookingId || "N/A"}</small>
                              </td>
                              <td>
                                {v.sourceCity} - {v.destinationCity}
                                <br />
                                <small>{moment(v.departureDate).format("DD MMM YYYY")}</small>
                              </td>
                              <td>
                                {v.operatorName}
                                <br />
                                <small>{v.busType}</small>
                                <br />
                                <small>{v.journeyType}</small>
                              </td>
                              <td>
                                {v.passengerName}
                                <br />
                                <small>Passengers: {v.noOfPassengers}</small>
                                <br />
                                <small>{v.customerPhone}</small>
                              </td>
                              <td>
                                <span className={`badge bg-${getStatusBadgeColor(v.bookingStatus)}`}>
                                  {v.bookingStatus}
                                </span>
                              </td>
                              <td>
                                <span className={`badge bg-${getPaymentBadgeColor(v.paymentStatus)}`}>
                                  {v.paymentStatus}
                                </span>
                              </td>

                              <td>
                                {formatCurrency(Math.round(v.totalPrice || 0))}
                              </td>
                              <td>
                                <ul className="list-inline hstack gap-2 mb-0">
                                  {v.bookingStatus === "BLOCKED" && (
                                    <li className="list-inline-item">
                                      <button
                                        onClick={(e) => handleConfirm(e, v.busBookingId)}
                                        className="btn btn-sm btn-secondary"
                                      >
                                        CONFIRM
                                      </button>
                                    </li>
                                  )}
                                  <li className="list-inline-item">
                                    <Link
                                      to={`/bus/booking/${v.busBookingId}`}
                                      className="btn btn-sm btn-primary"
                                    >
                                      <i className="ri-eye-line"></i>
                                    </Link>
                                  </li>
                                  <li className="list-inline-item">
                                    <button
                                      onClick={() => handleEditClick(v)}
                                      className="btn btn-sm btn-warning"
                                    >
                                      <i className="ri-edit-2-line"></i>
                                    </button>
                                  </li>
                                  {/* Email Dropdown */}
                                  <li className="list-inline-item">
                                    <div className="dropdown">
                                      <button
                                        className="btn btn-sm btn-info dropdown-toggle"
                                        type="button"
                                        id={`emailDropdown${v.busBookingId}`}
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                      >
                                        <i className="ri-mail-send-line"></i>
                                      </button>
                                      <ul className="dropdown-menu" aria-labelledby={`emailDropdown${v.busBookingId}`}>
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={() => handleSendCustomerEmail(v.busBookingId)}
                                          >
                                            <i className="ri-user-line me-2"></i>
                                            Send to Customer
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={() => handleSendAgentEmail(v.busBookingId)}
                                          >
                                            <i className="ri-user-settings-line me-2"></i>
                                            Send to Agent
                                          </button>
                                        </li>
                                      </ul>
                                    </div>
                                  </li>
                                  {/* Delete Button - Added this */}
                                  {v.bookingStatus !== "BOOKED" && v.paymentStatus !== "SUCCESS" && (
                                    <li className="list-inline-item">
                                      <button
                                        onClick={() => handleDelete(v.busBookingId, v.bookingNo)}
                                        className="btn btn-sm btn-danger"
                                        title="Delete Booking"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </li>
                                  )}
                                </ul>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={10} className="text-center">
                              No bus bookings found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* --- Pagination Footer --- */}
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing <span className="fw-semibold">{lists.length}</span> of{" "}
                        <span className="fw-semibold">{totalRecords}</span> Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li
                        className={`page-item ${rawResponse.previousPage === null ? "disabled" : ""}`}
                        onClick={GetFirstPageData}
                      >
                        <Link className="page-link">First</Link>
                      </li>
                      {pageNumbers.map((number) => (
                        <li
                          key={number}
                          className={`page-item ${pageNumber === number ? "active" : ""}`}
                          onClick={() => GetPageData(number)}
                        >
                          <Link className="page-link">{number}</Link>
                        </li>
                      ))}
                      <li
                        className={`page-item ${rawResponse.nextPage === null ? "disabled" : ""}`}
                        onClick={GetLastPageData}
                      >
                        <Link className="page-link">Last</Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Email Modal --- */}
      {showEmailModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="ri-mail-send-line me-2"></i>
                  Sending Email to {emailType === "customer" ? "Customer" : "Agent"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseEmailModal}
                  disabled={emailLoading}
                ></button>
              </div>
              <div className="modal-body text-center py-4">
                {emailLoading ? (
                  <>
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="mb-2">Sending Email...</h5>
                    <p className="text-muted">Please wait while we send the email to the {emailType === "customer" ? "customer" : "agent"}</p>
                    <div className="progress mt-3">
                      <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%' }}></div>
                    </div>
                  </>
                ) : emailSuccess ? (
                  <>
                    <div className="mb-3">
                      <i className="ri-checkbox-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h5 className="text-success mb-2">Email Sent Successfully!</h5>
                    <p className="text-muted">The email has been sent to the {emailType === "customer" ? "customer" : "agent"} successfully.</p>
                    <button
                      className="btn btn-success mt-3"
                      onClick={handleCloseEmailModal}
                    >
                      <i className="ri-check-line me-2"></i>
                      Done
                    </button>
                  </>
                ) : emailError ? (
                  <>
                    <div className="mb-3">
                      <i className="ri-error-warning-fill text-danger" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h5 className="text-danger mb-2">Failed to Send Email</h5>
                    <p className="text-muted">{emailError}</p>
                    <button
                      className="btn btn-danger mt-3"
                      onClick={handleCloseEmailModal}
                    >
                      <i className="ri-close-line me-2"></i>
                      Close
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Edit Modal --- */}
      {showEditModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="ri-edit-box-line me-2"></i>
                  Edit Bus Booking #{editBooking.bookingId}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  disabled={editLoading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {/* Personal Information */}
                  <div className="col-12">
                    <h6 className="border-bottom pb-2">Personal Information</h6>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Passenger Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="passengerName"
                      value={editBooking.passengerName}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Customer Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      name="customerPhone"
                      value={editBooking.customerPhone}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">PNR</label>
                    <input
                      type="text"
                      className="form-control"
                      name="pnr"
                      value={editBooking.pnr}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Supplier Booking ID</label>
                    <input
                      type="text"
                      className="form-control"
                      name="supplierBookingId"
                      value={editBooking.supplierBookingId}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Supplier Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="supplierName"
                      value={editBooking.supplierName}
                      onChange={handleEditChange}
                    />
                  </div>

                  {/* Route Information */}
                  <div className="col-12 mt-3">
                    <h6 className="border-bottom pb-2">Route Information</h6>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Source City</label>
                    <input
                      type="text"
                      className="form-control"
                      name="sourceCity"
                      value={editBooking.sourceCity}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Destination City</label>
                    <input
                      type="text"
                      className="form-control"
                      name="destinationCity"
                      value={editBooking.destinationCity}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Boarding Point</label>
                    <input
                      type="text"
                      className="form-control"
                      name="boardingPoint"
                      value={editBooking.boardingPoint}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Dropping Point</label>
                    <input
                      type="text"
                      className="form-control"
                      name="droppingPoint"
                      value={editBooking.droppingPoint}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Departure Date & Time</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      name="departureDate"
                      value={editBooking.departureDate}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">No. of Passengers</label>
                    <input
                      type="number"
                      className="form-control"
                      name="noOfPassengers"
                      value={editBooking.noOfPassengers}
                      onChange={handleEditChange}
                    />
                  </div>

                  {/* Bus Information */}
                  <div className="col-12 mt-3">
                    <h6 className="border-bottom pb-2">Bus Information</h6>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Operator Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="operatorName"
                      value={editBooking.operatorName}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Bus Type</label>
                    <input
                      type="text"
                      className="form-control"
                      name="busType"
                      value={editBooking.busType}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Journey Type</label>
                    <select
                      className="form-select"
                      name="journeyType"
                      value={editBooking.journeyType}
                      onChange={handleEditChange}
                    >
                      <option value="">Select Journey Type</option>
                      <option value="ONE_WAY">One Way</option>
                      <option value="ROUND_TRIP">Round Trip</option>
                      <option value="MULTI_CITY">Multi City</option>
                    </select>
                  </div>

                  {/* Status Information */}
                  <div className="col-12 mt-3">
                    <h6 className="border-bottom pb-2">Status Information</h6>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Booking Status</label>
                    <select
                      className="form-select"
                      name="bookingStatus"
                      value={editBooking.bookingStatus}
                      onChange={handleEditChange}
                    >
                      <option value="">Select Booking Status</option>
                      <option value="BOOKED">BOOKED</option>
                      <option value="CONFIRMED">CONFIRMED</option>
                      <option value="BLOCKED">BLOCKED</option>
                      <option value="HOLD">HOLD</option>
                      <option value="PENDING">PENDING</option>
                      <option value="CANCELLED">CANCELLED</option>
                      <option value="FAILED">FAILED</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Payment Status</label>
                    <select
                      className="form-select"
                      name="paymentStatus"
                      value={editBooking.paymentStatus}
                      onChange={handleEditChange}
                    >
                      <option value="">Select Payment Status</option>
                      <option value="SUCCESS">SUCCESS</option>
                      <option value="PAID">PAID</option>
                      <option value="PENDING">PENDING</option>
                      <option value="FAILED">FAILED</option>
                    </select>
                  </div>

                  {/* Payment Information */}
                  <div className="col-12 mt-3">
                    <h6 className="border-bottom pb-2">Payment Information</h6>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Payment Gateway</label>
                    <select
                      className="form-select"
                      name="paymentGateway"
                      value={editBooking.paymentGateway}
                      onChange={handleEditChange}
                    >
                      <option value="">Select Payment Gateway</option>
                      <option value="RAZORPAY">Razorpay</option>
                      <option value="WALLET">Wallet</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Gateway Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="paymentGatewayAmount"
                      value={editBooking.paymentGatewayAmount}
                      onChange={handleEditChange}
                    />
                  </div>

                  {/* Fare Breakdown */}
                  <div className="col-12 mt-3">
                    <h6 className="border-bottom pb-2">Fare Breakdown</h6>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Base Fare</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="baseFare"
                      value={editBooking.baseFare}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Tax</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="tax"
                      value={editBooking.tax}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Convenience Fee</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="convenienceFee"
                      value={editBooking.convenienceFee}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">GST</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="gst"
                      value={editBooking.gst}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">GST Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="gstAmount"
                      value={editBooking.gstAmount}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Markup</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="markup"
                      value={editBooking.markup}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Discount</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="discount"
                      value={editBooking.discount}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Total Fare</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="totalFare"
                      value={editBooking.totalFare}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Total Price</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="totalPrice"
                      value={editBooking.totalPrice}
                      onChange={handleEditChange}
                    />
                  </div>

                  {/* Passenger Details */}
                  <div className="col-12 mt-3">
                    <h6 className="border-bottom pb-2">Passenger Details</h6>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Passenger Details (JSON)</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      name="passengerDetails"
                      value={editBooking.passengerDetails}
                      onChange={handleEditChange}
                      placeholder="Enter passenger details in JSON format"
                    ></textarea>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-danger"
                  onClick={handleCloseModal}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                  disabled={editLoading}
                >
                  {editLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusBookings;